import './style.scss'
import React, { useContext, useEffect, useState } from 'react'
import { DateRange } from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import { FiFileText, FiChevronLeft, FiArrowRight } from 'react-icons/fi'
import utility from 'src/utils/utility'
import {
    Box, Flex, Text, tokens,
    Button, SearchField, FilterDropdown, Toolbar,
    InstitutionTable, TableColumn, TablePagination,
    Card, DateRangeFilterButton,
} from 'src/componentv2'
import { fontBold, fontMedium, fontRegular,PAGE_PADDING_X } from 'src/model/v2/interface/constants'
import ConfigurationApiContext, {
    AuditLogListItem,
    AuditLogsMeta,
    AuditLogDetailData,
    TEAM_ROLE_LABELS,
} from 'src/provider/API/v2/call-service/configuration'
import { actionPillColors, formatAuditTimestamp } from 'src/app/dashboardv2/shared/auditFormatting'

const PAGE_SIZE = 5
const SEARCH_DEBOUNCE_MS = 400

const ROLE_OPTIONS = Object.entries(TEAM_ROLE_LABELS).map(([value, label]) => ({ label, value }))

function humanize(name: string) {
    return name.replace(/([a-z])([A-Z])/g, '$1 $2')
}

// Adds one day so a picked "To" date includes the entirety of that day
// (backend upper bound is exclusive) — same convention as InstitutionList.
function toExclusiveEndOfDay(date: Date): string {
    const d = new Date(date)
    d.setDate(d.getDate() + 1)
    d.setHours(0, 0, 0, 0)
    return d.toISOString()
}

function parseJsonSafely(raw: string): Record<string, any> {
    try {
        const parsed = JSON.parse(raw)
        return typeof parsed === 'object' && parsed !== null ? parsed : { value: parsed }
    } catch {
        return { raw }
    }
}

function stringifyValue(value: any): string {
    if (value === null || value === undefined) return '—'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
}

function DiffCard({ label, data, variant }: { label: string; data: Record<string, any>; variant: 'before' | 'after' }) {
    const isAfter = variant === 'after'
    const bg = isAfter ? '#ECFDF3' : '#FEF3F2'
    const border = isAfter ? '#ABEFC6' : '#FECDCA'
    const titleColor = isAfter ? '#027A48' : '#B42318'
    const entries = Object.entries(data)

    return (
        <Box
            radius="0.8"
            style={{ background: bg, border: `1px solid ${border}`, padding: '1.6rem', flex: 1, minWidth: 0 }}
        >
            <Text fontSize="1.4" fontWeight="600" color={titleColor}>{label}</Text>
            <Flex direction="column" gap="0.8" topMargin="1.2">
                {entries.length === 0 && (
                    <Text fontSize="1.3" color={tokens.textMuted}>No data</Text>
                )}
                {entries.map(([key, value]) => (
                    <Flex key={key} justify="space-between" gap="1.2">
                        <Text fontSize="1.3" color={tokens.textMuted} style={{ flexShrink: 0 }}>
                            {humanize(key)}
                        </Text>
                        <Text fontSize="1.3" fontWeight="600" color={tokens.textDark} style={{ textAlign: 'right', wordBreak: 'break-word' }}>
                            {stringifyValue(value)}
                        </Text>
                    </Flex>
                ))}
            </Flex>
        </Box>
    )
}

function AuditActionPill({ action }: { action: string }) {
    const pill = actionPillColors(action)
    return (
        <span
            style={{
                display: 'inline-block',
                padding: '0.2rem 1rem',
                borderRadius: '9999px',
                fontSize: '1.3rem',
                fontWeight: 600,
                background: pill.bg,
                color: pill.color,
            }}
        >
            {action}
        </span>
    )
}

function AuditTrailDetail({ entry, onBack }: { entry: AuditLogListItem; onBack: () => void }) {
    const { getAuditLogDetail } = useContext(ConfigurationApiContext)
    const [detail, setDetail] = useState<AuditLogDetailData | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        setIsLoading(true)
        getAuditLogDetail(entry.id, entry.activityType).then((data: AuditLogDetailData | null) => {
            setDetail(data)
            setIsLoading(false)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entry.id, entry.activityType])

    const before = detail ? parseJsonSafely(detail.dataBefore) : {}
    const after = detail ? parseJsonSafely(detail.dataAfter) : {}
    const changedKeys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))

    return (
        <div style={{ padding: '2.4rem 3.2rem' }}>
            <Flex items="center" gap="1.2">
                <button
                    type="button"
                    onClick={onBack}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 'none', cursor: 'pointer', color: tokens.textMuted }}
                >
                    <FiChevronLeft size={18} />
                </button>
                <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>Audit Trail Detail</Text>
            </Flex>

            <Card topMargin="2.4" radius="1.2" borderColor={tokens.border} shadow={tokens.shadowCard} padding="2rem">
                {isLoading || !detail ? (
                    <Text fontSize="1.4" color={tokens.textMuted}>Loading audit entry...</Text>
                ) : (
                    <>
                        <div className="audit-trail__meta-grid">
                            {[
                                { label: 'Audit ID', value: String(entry.id) },
                                { label: 'Timestamp', value: formatAuditTimestamp(detail.performedAt) },
                                { label: 'Performed By', value: `${detail.fullName} (${detail.role})` },
                                { label: 'Action', value: detail.action },
                                { label: 'Module', value: humanize(detail.model) },
                                { label: 'Activity Type', value: detail.activityType },
                            ].map(({ label, value }) => (
                                <Flex key={label} direction="column" gap="0.2" items="flex-start">
                                    <Text fontSize="1.2" color={tokens.textMuted}>{label}</Text>
                                    <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>{value}</Text>
                                </Flex>
                            ))}
                        </div>

                        <Box topMargin="2.4">
                            <Text fontSize="1.6" style={fontMedium} color={tokens.textDark}>Data comparison</Text>
                            <Flex topMargin="1.2" gap="1.6" items="stretch" wrap="wrap">
                                <DiffCard label="Before" data={before} variant="before" />
                                <Flex items="center" justify="center" style={{ flexShrink: 0 }}>
                                    <FiArrowRight size={18} color={tokens.textMuted} />
                                </Flex>
                                <DiffCard label="After" data={after} variant="after" />
                            </Flex>
                        </Box>

                        <Box topMargin="2.4">
                            <Text fontSize="1.6" style={fontMedium} color={tokens.textDark}>Change summary</Text>
                            <Flex direction="column" gap="0.8" topMargin="1.2">
                                {changedKeys.map((key) => {
                                    const oldVal = stringifyValue(before[key])
                                    const newVal = stringifyValue(after[key])
                                    const changed = oldVal !== newVal
                                    return (
                                        <Flex
                                            key={key}
                                            items="center"
                                            gap="1.2"
                                            style={{
                                                padding: '1.2rem',
                                                borderRadius: '0.8rem',
                                                background: changed ? '#FFFAEB' : tokens.pageBg,
                                                border: changed ? '1px solid #FEDF89' : 'none',
                                            }}
                                        >
                                            <Text fontSize="1.3" color={tokens.textDark} style={{ flex: 1 }}>{humanize(key)}</Text>
                                            {key in before && <Text fontSize="1.3" color="#B42318" style={{ textDecoration: 'line-through' }}>{oldVal}</Text>}
                                            {key in before && <FiArrowRight size={13} color={tokens.textMuted} />}
                                            <Text fontSize="1.3" fontWeight="600" color="#027A48">{newVal}</Text>
                                        </Flex>
                                    )
                                })}
                            </Flex>
                        </Box>
                    </>
                )}
            </Card>
        </div>
    )
}

const AuditTrailList = () => {
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [selectedActions, setSelectedActions] = useState<string[]>([])
    const [selectedModules, setSelectedModules] = useState<string[]>([])
    const [selectedRoles, setSelectedRoles] = useState<string[]>([])
    const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined)
    const [page, setPage] = useState(1)

    const [actionOptions, setActionOptions] = useState<{ label: string; value: string }[]>([])
    const [moduleOptions, setModuleOptions] = useState<{ label: string; value: string }[]>([])

    const [items, setItems] = useState<AuditLogListItem[]>([])
    const [meta, setMeta] = useState<AuditLogsMeta | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [selectedEntry, setSelectedEntry] = useState<AuditLogListItem | null>(null)

    const context = useContext(ConfigurationApiContext)

    const {
        getAuditLogs,
        getAuditLogModels,
        getAuditLogActions,
    } = context || {}

    // Filter option lists only need to load once — actions/modules are fixed enums.
    useEffect(() => {
        if (getAuditLogActions && typeof getAuditLogActions === 'function') {
            getAuditLogActions().then((values: string[]) => {
                setActionOptions(values.map((v) => ({ label: v, value: v })))
            })
        }
        if (getAuditLogModels && typeof getAuditLogModels === 'function') {
            getAuditLogModels().then((values: string[]) => {
                setModuleOptions(values.map((v) => ({ label: humanize(v), value: v })))
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timeout)
    }, [search])

    useEffect(() => {
        setPage(1)
    }, [debouncedSearch, selectedActions, selectedModules, selectedRoles, dateRange])

    useEffect(() => {
        // Check if getAuditLogs is available
        if (!getAuditLogs || typeof getAuditLogs !== 'function') {
            setIsLoading(false)
            return
        }

        let cancelled = false
        setIsLoading(true)

        getAuditLogs({
            search: debouncedSearch || undefined,
            actions: selectedActions.length ? selectedActions : undefined,
            modules: selectedModules.length ? selectedModules : undefined,
            roles: selectedRoles.length ? selectedRoles : undefined,
            from: dateRange?.from ? dateRange.from.toISOString() : undefined,
            to: dateRange?.to ? toExclusiveEndOfDay(dateRange.to) : undefined,
            PageLimit: PAGE_SIZE,
            PageNumber: page,
        }).then(({ items: resultItems, meta: resultMeta }: { items: AuditLogListItem[]; meta: AuditLogsMeta | null }) => {
            if (cancelled) return
            setItems(resultItems)
            setMeta(resultMeta)
            setIsLoading(false)
        })

        return () => { cancelled = true }
        // NOTE: `getAuditLogs` is deliberately excluded from this dependency array.
        // The context provider rebuilds its function references on every render
        // (they aren't memoized with useCallback), so including it here caused
        // an infinite fetch loop: fetch -> loader state change -> provider
        // re-render -> new getAuditLogs reference -> effect fires again.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, selectedActions, selectedModules, selectedRoles, dateRange, page])

    const hasActiveFilters = selectedActions.length > 0 || selectedModules.length > 0
        || selectedRoles.length > 0 || !!dateRange?.from || !!debouncedSearch

    const clearFilters = () => {
        setSelectedActions([])
        setSelectedModules([])
        setSelectedRoles([])
        setDateRange(undefined)
        setSearch('')
    }

    // Check if getAuditLogs exists - if not, show error UI
    if (!getAuditLogs || typeof getAuditLogs !== 'function') {
        return (
            <div style={{ padding: '2.4rem 3.2rem' }}>
                <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>
                    Audit Trail Unavailable
                </Text>
                <Text fontSize="1.4" color={tokens.textMuted}>
                    The audit trail feature is currently unavailable. Please try again later.
                </Text>
            </div>
        )
    }

    if (selectedEntry) {
        return <AuditTrailDetail entry={selectedEntry} onBack={() => setSelectedEntry(null)} />
    }

    const columns: TableColumn<AuditLogListItem>[] = [
        {
            key: 'sn',
            title: 'S/N',
            width: '6rem',
            render: (row) => <Text fontSize="1.3" color={tokens.textMuted}>{row.sn}</Text>,
        },
        {
            key: 'timestamp',
            title: 'Timestamp',
            render: (row) => (
                <Text fontSize="1.3" color={tokens.textMuted} style={{ whiteSpace: 'nowrap' }}>
                    {formatAuditTimestamp(row.performedAt)}
                </Text>
            ),
        },
        {
            key: 'user',
            title: 'User',
            render: (row) => (
                <Flex direction="column" gap="0.1" items="flex-start">
                    <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>{row.fullName}</Text>
                    <Text fontSize="1.2" color={tokens.textMuted}>{row.role}</Text>
                </Flex>
            ),
        },
        {
            key: 'module',
            title: 'Module',
            render: (row) => <Text fontSize="1.3" color={tokens.textDark}>{humanize(row.model)}</Text>,
        },
        {
            key: 'action',
            title: 'Action',
            render: (row) => <AuditActionPill action={row.action} />,
        },
        {
            key: 'actions',
            title: '',
            width: '13rem',
            render: (row) => (
                <Text
                    fontSize="1.4"
                    fontWeight="600"
                    color={tokens.primary}
                    style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
                    onClick={() => setSelectedEntry(row)}
                >
                    View Details
                </Text>
            ),
        },
    ]

    return (
        <div style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
            <Flex direction="column" gap="0.6" items="flex-start">
                <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>
                    Audit Trail
                </Text>
                <Text fontSize="1.4" color={tokens.textMuted} style={fontRegular}>
                    Complete log of all activity, changes, and access across this institution
                </Text>
            </Flex>

            <Box
                topMargin="2.4"
                bgColor={tokens.white}
                borderColor={tokens.border}
                radius="1.2"
                shadow={tokens.shadowCard}
                style={{ padding: '0 1.6rem' }}
            >
                <Toolbar
                    left={(
                        <>
                            <SearchField width="28rem" value={search} onChange={setSearch} placeholder="Search by user, action..." />
                            <FilterDropdown
                                label="Action"
                                options={actionOptions}
                                value={selectedActions}
                                onChange={setSelectedActions}
                            />
                            <FilterDropdown
                                label="Module"
                                options={moduleOptions}
                                value={selectedModules}
                                onChange={setSelectedModules}
                            />
                            <FilterDropdown
                                label="Role"
                                options={ROLE_OPTIONS}
                                value={selectedRoles}
                                onChange={setSelectedRoles}
                                footer={(
                                    <div className="audit-trail__dropdown-date">
                                        <Text fontSize="1.3" fontWeight="600" color={tokens.textMuted}>
                                            Date range
                                        </Text>
                                        <DateRangeFilterButton value={dateRange} onChange={setDateRange} />
                                    </div>
                                )}
                            />
                            <Button variant="outline" disabled={!hasActiveFilters} onClick={clearFilters}>
                                Clear Filters
                            </Button>
                        </>
                    )}
                />
            </Box>

            <Box
                topMargin="1.6"
                bgColor={tokens.white}
                borderColor={tokens.border}
                radius="1.2"
                shadow={tokens.shadowCard}
                style={{ overflow: 'hidden' }}
            >
                <InstitutionTable
                    columns={columns}
                    data={items}
                    loading={isLoading}
                    emptyText={(
                        <Flex direction="column" items="center" gap="0.8" style={{ padding: '2.4rem 0' }}>
                            <FiFileText size={36} color={tokens.textMuted} style={{ opacity: 0.4 }} />
                            <Text fontSize="1.4" color={tokens.textMuted}>No audit records found</Text>
                        </Flex>
                    )}
                />

                <div style={{ borderTop: '1px solid #EAECF0' }} />

                <Box padding="1.6">
                    <TablePagination
                        page={meta?.currentPageNumber ?? page}
                        total={meta?.total ?? 0}
                        pageSize={meta?.perPage ?? PAGE_SIZE}
                        onPageChange={setPage}
                    />
                </Box>
            </Box>
        </div>
    )
}

export default utility.routeData(
    'audit-trail',
    'Audit Trail',
    <AuditTrailList />,
)